import React from 'react';
import { Menu, MenuItem, Divider, Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { getUserRoles, ROLE_IDS } from '../services/auth';
import { useTour } from '../contexts/TourContext';
import { MessageSquare, History, Briefcase, Settings, LogOut, User, Menu as MenuIcon, LayoutDashboard, Users, Star, HelpCircle, X, Shield, Bug, Lightbulb } from 'lucide-react';
import { FeedbackDialog } from './FeedbackDialog';
import { ROLE_TYPES } from '../config/roleTypes';
import { useThemeMode } from '../contexts/ThemeContext';

interface NavigationProps {
}

// Helper function to get display name for role type
const getRoleTypeDisplayName = (roleTypeId: string | null): string => {
  if (!roleTypeId) return 'No role set';
  const roleConfig = ROLE_TYPES[roleTypeId];
  return roleConfig?.displayName || roleTypeId;
};

export const Navigation: React.FC<NavigationProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loginWithRedirect, logout, user, getAccessTokenSilently } = useAuth0();
  const { startTour } = useTour();
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';
  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [currentCandidateType, setCurrentCandidateType] = React.useState<string | null>(null);
  const [isRecruiter, setIsRecruiter] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = React.useState(false);
  const [feedbackType, setFeedbackType] = React.useState<'bug' | 'feature'>('bug');
  const [isPro, setIsPro] = React.useState(false);

  React.useEffect(() => {
    const checkAuth = async () => {
      if (isAuthenticated && user?.sub) {
        try {
          // Get user roles from the API (using cached version)
          const roles = await getUserRoles(user.sub, getAccessTokenSilently);

          // Check if user is a recruiter
          const hasRecruiterRole = roles.some(role => role.id === ROLE_IDS.RECRUITER);
          setIsRecruiter(hasRecruiterRole);

          // Fetch candidate type from user metadata
          const token = await getAccessTokenSilently();
          const metadataResponse = await fetch(`${import.meta.env.VITE_API_URL || 'https://backend.veltus.io'}/auth/user-metadata/${user.sub}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (metadataResponse.ok) {
            const { metadata } = await metadataResponse.json();
            const type = metadata.candidate_type;
            const adminStatus = metadata.admin || false;

            // Set admin status
            setIsAdmin(adminStatus);

            if (type) {
              setCurrentCandidateType(type);
              localStorage.setItem('candidateType', type);
            } else {
              // Check localStorage for existing candidate type
              const storedType = localStorage.getItem('candidateType');
              if (storedType) {
                setCurrentCandidateType(storedType);
              }
            }
          } else {
            // Check localStorage for existing candidate type
            const storedType = localStorage.getItem('candidateType');
            if (storedType) {
              setCurrentCandidateType(storedType);
            }
            setIsAdmin(false);
          }
          // Check Pro subscription status
          try {
            const subResponse = await fetch(`${import.meta.env.VITE_API_URL || 'https://backend.veltus.io'}/api/payments/subscription-status`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (subResponse.ok) {
              const subData = await subResponse.json();
              setIsPro(subData.is_pro === true);
            }
          } catch {
            // Non-critical - don't block navigation
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
      } else {
        // Clear the candidate type when logged out
        localStorage.removeItem('candidateType');
        setCurrentCandidateType(null);
        setIsRecruiter(false);
        setIsAdmin(false);
        setIsPro(false);
      }
    };

    checkAuth();
  }, [isAuthenticated, user?.sub, getAccessTokenSilently]);

  const handleLogin = () => {
    loginWithRedirect({
      authorizationParams: {
        audience: 'https://veltus.au.auth0.com/api/v2/',
        scope: 'openid profile email roles'
      }
    });
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleViewWalkthrough = () => {
    handleUserMenuClose();
    // Navigate to dashboard first, then start tour after a delay
    // This ensures the dashboard and all tour target elements are rendered
    navigate('/dashboard');
    setTimeout(() => {
      startTour();
    }, 500);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
    <nav className={isDark ? "bg-[#1E1E1E]/90 backdrop-blur-md border-b border-white/10" : "bg-white/80 backdrop-blur-md border-b border-gray-200"}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Mobile Menu Button */}
          {isAuthenticated && (
            <div className="md:hidden">
              <IconButton
                onClick={handleMobileMenuToggle}
                sx={{ color: 'rgb(17 24 39)' }}
                aria-label="menu"
              >
                <MenuIcon size={24} />
              </IconButton>
            </div>
          )}

          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => navigate(isRecruiter ? '/recruiter-dashboard' : '/dashboard')}
              className={`text-2xl font-bold transition-colors ${isDark ? 'text-gray-100 hover:text-gray-300' : 'text-gray-900 hover:text-gray-700'}`}
            >
              OnSite
            </button>
          </div>

          {/* Navigation Links */}
          {isAuthenticated && (
            <div className="hidden md:flex items-center gap-1">
              {isRecruiter ? (
                // Recruiter Navigation
                <>
                  <button
                    onClick={() => navigate('/recruiter-dashboard')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === '/recruiter-dashboard'
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/recruiter/jobs')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname.startsWith('/recruiter/jobs')
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <Briefcase className="w-4 h-4" />
                    Jobs
                  </button>
                  <button
                    onClick={() => navigate('/recruiter/talent')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname.startsWith('/recruiter/talent')
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <Users className="w-4 h-4" />
                    Talent
                  </button>
                  <button
                    onClick={() => navigate('/recruiter/shortlist')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname.startsWith('/recruiter/shortlist')
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <Star className="w-4 h-4" />
                    Shortlist
                  </button>
                  <button
                    onClick={() => navigate('/settings')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === '/settings'
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => navigate('/admin/moderation')}
                      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        location.pathname === '/admin/moderation'
                          ? 'bg-red-100 text-red-900'
                          : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                      }`}
                    >
                      <Shield className="w-4 h-4" />
                      Admin
                    </button>
                  )}
                </>
              ) : (
                // Interviewer Navigation
                <>
                  <button
                    onClick={() => navigate('/dashboard')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === '/dashboard'
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/interviews')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname.startsWith('/interview')
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    Interviews
                  </button>
                  <button
                    onClick={() => navigate('/interview-history')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === '/interview-history'
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <History className="w-4 h-4" />
                    History
                  </button>
                  <button
                    onClick={() => navigate('/role-prep')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname.startsWith('/role-prep')
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <Briefcase className="w-4 h-4" />
                    Role Prep
                  </button>
                  <button
                    onClick={() => navigate('/settings')}
                    className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      location.pathname === '/settings'
                        ? (isDark ? 'bg-white/10 text-gray-100' : 'bg-gray-100 text-gray-900')
                        : (isDark ? 'text-gray-400 hover:text-gray-100 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50')
                    }`}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => navigate('/admin/moderation')}
                      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                        location.pathname === '/admin/moderation'
                          ? 'bg-red-100 text-red-900'
                          : 'text-red-600 hover:text-red-900 hover:bg-red-50'
                      }`}
                    >
                      <Shield className="w-4 h-4" />
                      Admin
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* User Menu / Auth */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <button
                  onClick={handleUserMenuClick}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${isDark ? 'text-gray-300 hover:text-gray-100 hover:bg-white/5' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'}`}
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{user?.email?.split('@')[0]}</span>
                  {isPro && (
                    <span className="hidden sm:inline px-1.5 py-0.5 text-xs font-bold bg-amber-400 text-amber-900 rounded-full">
                      PRO
                    </span>
                  )}
                  <MenuIcon className="w-4 h-4" />
                </button>
                <Menu
                  anchorEl={userMenuAnchorEl}
                  open={Boolean(userMenuAnchorEl)}
                  onClose={handleUserMenuClose}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      borderRadius: 2,
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                      border: '1px solid',
                      borderColor: 'rgba(0, 0, 0, 0.08)',
                    }
                  }}
                >
                  <MenuItem disabled sx={{ fontSize: '0.875rem', color: 'rgb(107 114 128)' }}>
                    {user?.email}
                  </MenuItem>
                  <MenuItem disabled sx={{ fontSize: '0.875rem', color: 'rgb(107 114 128)' }}>
                    {isRecruiter ? 'Recruiter' : getRoleTypeDisplayName(currentCandidateType)}
                  </MenuItem>
                  {isPro && (
                    <MenuItem disabled sx={{ fontSize: '0.75rem', py: 0.5 }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '2px 8px',
                        background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                        color: '#fff',
                        borderRadius: 12,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        letterSpacing: '0.05em',
                      }}>
                        <Star className="w-3 h-3" /> OnSite Pro
                      </span>
                    </MenuItem>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      navigate('/settings');
                    }}
                    sx={{
                      fontSize: '0.875rem',
                      color: 'rgb(17 24 39)',
                      '&:hover': {
                        bgcolor: 'rgb(249 250 251)'
                      }
                    }}
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </MenuItem>
                  {!isRecruiter && (
                    <MenuItem
                      onClick={handleViewWalkthrough}
                      sx={{
                        fontSize: '0.875rem',
                        color: 'rgb(17 24 39)',
                        '&:hover': {
                          bgcolor: 'rgb(249 250 251)'
                        }
                      }}
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      View Walkthrough
                    </MenuItem>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      setFeedbackType('bug');
                      setFeedbackDialogOpen(true);
                    }}
                    sx={{
                      fontSize: '0.875rem',
                      color: 'rgb(17 24 39)',
                      '&:hover': {
                        bgcolor: 'rgb(249 250 251)'
                      }
                    }}
                  >
                    <Bug className="w-4 h-4 mr-2" />
                    Report a Bug
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      setFeedbackType('feature');
                      setFeedbackDialogOpen(true);
                    }}
                    sx={{
                      fontSize: '0.875rem',
                      color: 'rgb(17 24 39)',
                      '&:hover': {
                        bgcolor: 'rgb(249 250 251)'
                      }
                    }}
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Feature Request
                  </MenuItem>
                  <Divider sx={{ my: 1 }} />
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      handleLogout();
                    }}
                    sx={{
                      fontSize: '0.875rem',
                      color: 'rgb(17 24 39)',
                      '&:hover': {
                        bgcolor: 'rgb(249 250 251)'
                      }
                    }}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <button
                onClick={handleLogin}
                className={isDark ? "bg-gray-100 hover:bg-gray-200 text-gray-900 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200" : "bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200"}
              >
                Login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>

    {/* Mobile Menu Drawer */}
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          width: 280,
          bgcolor: 'white'
        }
      }}
    >
      <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center">
          <span className={`text-xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>OnSite</span>
        </div>
        <IconButton onClick={() => setMobileMenuOpen(false)} size="small">
          <X size={20} />
        </IconButton>
      </div>

      <div className={`p-3 border-b ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex items-center gap-2">
          <div className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{user?.email}</div>
          {isPro && (
            <span className="px-1.5 py-0.5 text-xs font-bold bg-amber-400 text-amber-900 rounded-full">
              PRO
            </span>
          )}
        </div>
        <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {isRecruiter ? 'Recruiter' : getRoleTypeDisplayName(currentCandidateType)}
        </div>
      </div>

      <List sx={{ px: 1, py: 2 }}>
        {isRecruiter ? (
          // Recruiter Navigation
          <>
            <ListItem
              onClick={() => handleMobileNavigation('/recruiter-dashboard')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname === '/recruiter-dashboard' ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LayoutDashboard size={20} />
              </ListItemIcon>
              <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            <ListItem
              onClick={() => handleMobileNavigation('/recruiter/jobs')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname.startsWith('/recruiter/jobs') ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Briefcase size={20} />
              </ListItemIcon>
              <ListItemText primary="Jobs" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            <ListItem
              onClick={() => handleMobileNavigation('/recruiter/talent')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname.startsWith('/recruiter/talent') ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Users size={20} />
              </ListItemIcon>
              <ListItemText primary="Talent" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            <ListItem
              onClick={() => handleMobileNavigation('/recruiter/shortlist')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname.startsWith('/recruiter/shortlist') ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Star size={20} />
              </ListItemIcon>
              <ListItemText primary="Shortlist" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            <ListItem
              onClick={() => handleMobileNavigation('/settings')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname === '/settings' ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Settings size={20} />
              </ListItemIcon>
              <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            {isAdmin && (
              <ListItem
                onClick={() => handleMobileNavigation('/admin/moderation')}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  bgcolor: location.pathname === '/admin/moderation' ? 'rgb(254 226 226)' : 'transparent',
                  color: location.pathname === '/admin/moderation' ? 'rgb(153 27 27)' : 'rgb(220 38 38)',
                  '&:hover': { bgcolor: 'rgb(254 242 242)' },
                  cursor: 'pointer'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'rgb(220 38 38)' }}>
                  <Shield size={20} />
                </ListItemIcon>
                <ListItemText primary="Admin" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }} />
              </ListItem>
            )}
          </>
        ) : (
          // Interviewer Navigation
          <>
            <ListItem
              onClick={() => handleMobileNavigation('/dashboard')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname === '/dashboard' ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LayoutDashboard size={20} />
              </ListItemIcon>
              <ListItemText primary="Dashboard" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            <ListItem
              onClick={() => handleMobileNavigation('/interviews')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname.startsWith('/interview') ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <MessageSquare size={20} />
              </ListItemIcon>
              <ListItemText primary="Interviews" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            <ListItem
              onClick={() => handleMobileNavigation('/interview-history')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname === '/interview-history' ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <History size={20} />
              </ListItemIcon>
              <ListItemText primary="History" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            <ListItem
              onClick={() => handleMobileNavigation('/role-prep')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname.startsWith('/role-prep') ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Briefcase size={20} />
              </ListItemIcon>
              <ListItemText primary="Role Prep" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            <ListItem
              onClick={() => handleMobileNavigation('/settings')}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                bgcolor: location.pathname === '/settings' ? 'rgb(243 244 246)' : 'transparent',
                '&:hover': { bgcolor: 'rgb(249 250 251)' },
                cursor: 'pointer'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Settings size={20} />
              </ListItemIcon>
              <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
            </ListItem>
            {isAdmin && (
              <ListItem
                onClick={() => handleMobileNavigation('/admin/moderation')}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  bgcolor: location.pathname === '/admin/moderation' ? 'rgb(254 226 226)' : 'transparent',
                  color: location.pathname === '/admin/moderation' ? 'rgb(153 27 27)' : 'rgb(220 38 38)',
                  '&:hover': { bgcolor: 'rgb(254 242 242)' },
                  cursor: 'pointer'
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'rgb(220 38 38)' }}>
                  <Shield size={20} />
                </ListItemIcon>
                <ListItemText primary="Admin" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }} />
              </ListItem>
            )}
          </>
        )}
      </List>

      <Divider />

      <List sx={{ px: 1, py: 2 }}>
        {!isRecruiter && (
          <ListItem
            onClick={() => {
              setMobileMenuOpen(false);
              handleViewWalkthrough();
            }}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              '&:hover': { bgcolor: 'rgb(249 250 251)' },
              cursor: 'pointer'
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <HelpCircle size={20} />
            </ListItemIcon>
            <ListItemText primary="View Walkthrough" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
          </ListItem>
        )}
        <ListItem
          onClick={() => {
            setMobileMenuOpen(false);
            setFeedbackType('bug');
            setFeedbackDialogOpen(true);
          }}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            '&:hover': { bgcolor: 'rgb(249 250 251)' },
            cursor: 'pointer'
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Bug size={20} />
          </ListItemIcon>
          <ListItemText primary="Report a Bug" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
        </ListItem>
        <ListItem
          onClick={() => {
            setMobileMenuOpen(false);
            setFeedbackType('feature');
            setFeedbackDialogOpen(true);
          }}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            '&:hover': { bgcolor: 'rgb(249 250 251)' },
            cursor: 'pointer'
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Lightbulb size={20} />
          </ListItemIcon>
          <ListItemText primary="Feature Request" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
        </ListItem>
        <ListItem
          onClick={() => {
            setMobileMenuOpen(false);
            handleLogout();
          }}
          sx={{
            borderRadius: 2,
            '&:hover': { bgcolor: 'rgb(249 250 251)' },
            cursor: 'pointer'
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <LogOut size={20} />
          </ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: '0.95rem', fontWeight: 500 }} />
        </ListItem>
      </List>
    </Drawer>

    <FeedbackDialog
      open={feedbackDialogOpen}
      onClose={() => setFeedbackDialogOpen(false)}
      type={feedbackType}
      userEmail={user?.email}
    />
    </>
  );
}; 